import type { ListField } from '@ztechapps/common.hook'
import { useDeepCompareEffect } from '@ztechapps/common.hook'
import { useTableContext } from '../Context'
import React, { useState } from 'react'
import map from 'lodash/map'
import isEqual from 'lodash/isEqual'
import filter from 'lodash/filter'
import { ListGroup } from 'react-bootstrap'
import {
  HorizontalStack,
  Icon,
  ShowIf,
  dragSortItemClassName,
  dragSortTrailingDropZoneClassName,
  useDragSort,
} from '@ztechapps/webui.util'
import { CheckboxInput } from '@ztechapps/webui.input'

type SortableItem = {
  id: string
} & ListField

export const ColumnList = () => {
  const { listFields, setFields, hasFilterValue } = useTableContext()

  const [sortableList, _setSortableList] = useState<SortableItem[]>([])

  //this re-initialize the sortableList
  useDeepCompareEffect(() => {
    //show only the fields that are for display
    const filteredList = filter(listFields, (f) => !f.excludeOnDisplay)
    const items = map(filteredList, (field) => ({
      id: field.name,
      ...field,
    }))
    _setSortableList(items)
  }, [listFields])

  const setSortableList = (newList: SortableItem[]) => {
    if (isEqual(newList, sortableList)) return //get out if the list is empty

    //we need to build the new index
    const obj = {}
    newList.forEach((item, index) => {
      obj[item.key] = { index }
    })
    setFields(obj)
    _setSortableList(newList)
  }

  const { getItemProps, getHandleProps, getContainerProps, dragState } = useDragSort<SortableItem>({
    items: sortableList,
    getItemKey: (field) => field.id,
    onSort: ({ oldIndex, newIndex }) => {
      const next = [...sortableList]
      const [moved] = next.splice(oldIndex, 1)
      next.splice(newIndex, 0, moved)
      setSortableList(next)
    },
  })

  return (
    <ListGroup variant="flush" {...getContainerProps()}>
      {map(sortableList, (field, index) => (
        <ListGroup.Item
          key={field.id}
          className={dragSortItemClassName}
          {...getItemProps(index)}
          {...getHandleProps(index)}
        >
          <HorizontalStack gap={2}>
            <div>
              <Icon name="bars" />
            </div>
            <CheckboxInput
              checked={field.isVisible}
              onChange={() => setFields({ [field.key]: { isVisible: !field.isVisible } })}
              label={field.displayName}
            />
            <ShowIf condition={hasFilterValue(field.name)}>
              <Icon name="filter" title="Has active filter" />
            </ShowIf>
          </HorizontalStack>
        </ListGroup.Item>
      ))}
      {/* Trailing overshoot buffer below the last row, present only mid-drag. */}
      <ShowIf condition={dragState.isDragging}>
        <ListGroup.Item aria-hidden="true" className={dragSortTrailingDropZoneClassName} />
      </ShowIf>
    </ListGroup>
  )
}
